import { ResponsiveLine } from "@nivo/line";
import { useTranslation } from "react-i18next";

import InfoIcon from "assets/icons/info.svg?react";

import { chartSmallConfig } from "config/chart";

import {
  convertValueToLocale,
  convertValueToLocaleForm,
} from "helpers/convertValueToLocale";

import {
  getChartData,
  getInfoPopoverTitle,
  getEvenlySpacedYears,
} from "./RentalIncomeCard.helpers";

import { Popover } from "shared/ui/components/Popover";
import { Typography } from "shared/ui/components/Typography";

import styles from "./RentalIncomeCard.module.scss";

const _renderInfoPopover = ({ t, info }: { t: any; info: any }) => {
  return (
    <div className={styles.popover}>
      <Typography variant="bodyS" weight="light">
        {getInfoPopoverTitle(t, info)}
      </Typography>
      {info.yearAptRentIncomes.map((item: any, index) => (
        <div key={index} className={styles.popoverInfoItem}>
          <Typography
            variant="bodyS"
            weight="semiBold"
            className={styles.popoverInfoItemTitle}
          >
            {t(
              `calculator_pro_page.total_income_chart.info.apt_info_popover_title`,
              {
                aptDowntimeLossPercent: info.aptDowntimeLossPercent,
              }
            )}
          </Typography>
          <Typography
            variant="bodyS"
            weight="light"
            className={styles.popoverInfoItemValue}
          >
            {t(
              "calculator_pro_page.total_income_chart.info.info_popover_description_dynamic",
              {
                rentGrowth: item.aptRentGrowth,
                yearRentIncome: convertValueToLocale(item.yearAptRentIncome),
                years: item.aptRentGrowthDynamic,
              }
            )}
          </Typography>
        </div>
      ))}
      {info.yearParkingRentIncomes.map((item: any, index: number) => (
        <div key={index} className={styles.popoverInfoItem}>
          <Typography
            variant="bodyS"
            weight="semiBold"
            className={styles.popoverInfoItemTitle}
          >
            {t(
              `calculator_pro_page.total_income_chart.info.parking_info_popover_title`,
              {
                parkingDowntimeLossPercent: info.parkingDowntimeLossPercent,
              }
            )}
          </Typography>
          <Typography
            variant="bodyS"
            weight="light"
            className={styles.popoverInfoItemValue}
          >
            {t(
              "calculator_pro_page.total_income_chart.info.info_popover_description_dynamic",
              {
                rentGrowth: item.parkingRentGrowth,
                yearRentIncome: convertValueToLocale(
                  item.yearParkingRentIncome
                ),
                years: item.parkingRentGrowthDynamic,
              }
            )}
          </Typography>
        </div>
      ))}
    </div>
  );
};

const CustomTooltip = ({ point }: { point: any }) => {
  const { data } = point;

  const year = data.x;
  const valueFormatted = convertValueToLocale(data.y);

  return (
    <div className={styles.tooltip}>
      <Typography variant="bodyS" weight="semiBold">
        {year}: {valueFormatted}
      </Typography>
    </div>
  );
};

const RentalIncomeCard = ({
  totalIncomeChart,
  activePosition,
}: {
  totalIncomeChart: any;
  activePosition: 10 | 20 | 30;
}) => {
  const { t } = useTranslation();

  const chartData = getChartData(totalIncomeChart, activePosition);
  const yearsToShow = getEvenlySpacedYears(totalIncomeChart, activePosition, 6);

  return (
    <div className={styles.chartItem}>
      <div className={styles.header}>
        <Typography
          className={styles.chartTitle}
          variant="bodyS"
          weight="semiBold"
        >
          {t("calculator_pro_page.total_income_chart.title")}
        </Typography>
        <Popover
          placement="top"
          triggerType="hover"
          yOffset={15}
          floatingArrowProps={{ width: 17, height: 10, fill: "#fff" }}
          triggerClassName={styles.trigger}
          triggerElement={() => <InfoIcon className={styles.infoIcon} />}
        >
          {_renderInfoPopover({ t, info: totalIncomeChart.info })}
        </Popover>
      </div>

      <Typography
        className={styles.chartTitlePositive}
        variant="bodyM"
        weight="semiBold"
      >
        {convertValueToLocale(
          totalIncomeChart.totalIncomesValueBrutto[activePosition]
        )}
      </Typography>

      <Typography variant="bodyXS" className={styles.chartDescription}>
        {t("calculator_pro_page.total_income_chart.description", {
          downtimePercentage: convertValueToLocaleForm(
            totalIncomeChart.info.aptDowntimeLossPercent
          ),
        })}
      </Typography>

      <div className={styles.chart}>
        <ResponsiveLine
          {...chartSmallConfig}
          data={[
            {
              id: "chart",
              data: chartData,
            },
          ]}
          yScale={{
            type: "linear",
            min: "auto",
            max: "auto",
            stacked: false,
            reverse: false,
          }}
          fill={[
            {
              id: "gradient-green",
              match: { id: "chart" },
            },
          ]}
          axisLeft={{
            tickSize: 0,
            tickPadding: 5,
            tickRotation: 0,
            format: () => "",
          }}
          axisBottom={{
            format: (year: number) => {
              return yearsToShow.includes(year) ? year : "";
            },
          }}
          enableTouchCrosshair={true}
          useMesh={true}
          tooltip={CustomTooltip}
        />
      </div>
    </div>
  );
};

export default RentalIncomeCard;
